/**
 * Reenviar el remito de una venta ya emitida.
 *
 * Es la respuesta a "el cliente me lo pidió al día siguiente", y es lo que hace
 * que el backfill de tokens de la migracion 0021 sirva para algo: los
 * comprobantes viejos tambien tienen link.
 *
 * TRES ACCIONES, TRES LINKS
 * -------------------------
 * Ver, mandar por WhatsApp y copiar. Las dos primeras son `<a>` de verdad y no
 * `window.open`: un link no lo bloquea ningun navegador, y ademas se puede
 * abrir con el boton del medio o copiar con el menu contextual.
 *
 * El de WhatsApp apunta a `/r/:token/whatsapp`, una ruta del backend con
 * sesion que busca el telefono en la ficha del cliente y redirige a su chat
 * con el mensaje ya escrito. Por eso este componente no necesita el telefono
 * ni el detalle de items: sin `await` no hay nada que iOS pueda bloquear, y el
 * numero del cliente no viaja en ningun HTML. Si el cliente no tiene telefono
 * cargado, la misma ruta abre el selector de contactos de WhatsApp.
 */
import { useEffect, useState } from "react";
import { Check, Copy, Eye, MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { urlRemito } from "@/lib/remito";

export interface AccionesRemitoProps {
  numero: string;
  token: string | null | undefined;
}

const BASE_PUBLICA = import.meta.env.VITE_URL_PUBLICA ?? window.location.origin;

export function AccionesRemito({ numero, token }: AccionesRemitoProps): JSX.Element | null {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);

  if (!token) return null;
  const url = urlRemito(BASE_PUBLICA, token);

  return (
    <>
      {/* Links con la caja del IconButton (36/32 px): son objetivos tactiles en la fila. */}
      <Button asChild variant="ghost" size="icon-sm">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Ver el remito de ${numero}`}
          title="Ver remito"
        >
          <Eye aria-hidden="true" />
        </a>
      </Button>

      <Button asChild variant="ghost" size="icon-sm">
        <a
          href={`${url}/whatsapp`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Mandar el remito de ${numero} por WhatsApp`}
          title="Mandar por WhatsApp"
        >
          <MessageCircle aria-hidden="true" />
        </a>
      </Button>

      <IconButton
        icono={copiado ? Check : Copy}
        tamano="sm"
        className={copiado ? "text-success" : undefined}
        onClick={() => {
          navigator.clipboard.writeText(url).then(
            () => setCopiado(true),
            () => undefined,
          );
        }}
        aria-label={`Copiar el link del remito de ${numero}`}
        title={copiado ? "Copiado" : "Copiar link"}
      />
    </>
  );
}
